import React, { useState } from 'react'
import moment from 'moment'

import { isNil, noop } from 'lodash-es'
import { Index, ValidationIssue, ValidationType } from 'api'
import { DialogField, DialogRow, NumericInput, NumericInputField, TextInputField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { formatPrice, formatValidFor, PRICE_FORMAT_OPTIONS, VALID_FOR_FORMAT } from '../IndexScreen/indexFormatters'
import { Locale, Translation } from 'locale'

import { iconSet } from 'view/components/icons'
import {
  Heading,
  NoPricesContainer,
  PriceAdder,
  PriceContainer,
  PriceEntry,
  PriceHeader,
  PriceRow,
  RemovePriceButton,
  StyledDatePicker,
  ValidationIssuesContainer
} from './IndexDialog.styles'
import { LagTypeSelectboxField } from 'view/shared/entity/LagTypeSelectbox'
import { useSelector } from 'react-redux'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { indexNameMaxFieldLength, isinMaxFieldLength, lagAmountMaxFieldLength } from 'view/styles/constants'
import { isNotEmpty } from 'utils/predicate'
import { createUserNameByUserIdGetterSelector } from 'selectors/userSelectors'

interface IndexOverviewPanelProps {
  index: Index
  readOnly: boolean
  validationIssues: ValidationIssue[] | null
  onUpdate: (partialIndex: Partial<Index>) => unknown
}

export const IndexOverviewPanel: React.FunctionComponent<IndexOverviewPanelProps> = props => {
  const { index, readOnly, validationIssues, onUpdate } = props
  const [newValidFor, setNewValidFor] = useState<string | null>(null)
  const [newPrice, setNewPrice] = useState<number | null>(null)
  const userPreference = useSelector(createUserPreferenceSelector())
  const getUserNameFromUserId = useSelector(createUserNameByUserIdGetterSelector)

  const updateHandler = <K extends keyof Index>(key: K) => {
    return (value: Index[K]) => {
      onUpdate({ [key]: isNotEmpty(value) || typeof value === 'number' ? value : null })
    }
  }

  const handleAddPrice = () => {
    if (!isNil(newValidFor) && !isNil(newPrice)) {
      const newValues = Object.keys(index.values).reduce((acc, indexDate) => {
        acc[
          moment(indexDate)
            .startOf('month')
            .format(VALID_FOR_FORMAT)
        ] = index.values[indexDate]
        return acc
      }, {} as Record<string, number>)

      const validForString = moment(newValidFor).format(VALID_FOR_FORMAT)
      newValues[validForString] = newPrice

      onUpdate({ values: newValues })
      setNewValidFor(null)
      setNewPrice(null)
    } else {
      throw new Error('Date and price must not be empty')
    }
  }

  const handleRemovePrice = (validFor: string) => {
    const newValues = { ...index.values }
    delete newValues[validFor]

    onUpdate({ values: newValues })
  }

  return (
    <>
      <DialogRow>
        <DialogField>
          <TextInputField
            fieldName="description"
            dataTestId={'IndexOverviewPanel-TextInputField-Description'}
            label={Locale.translate(Translation.TC_INDEX_DESCRIPTION)}
            value={index.description}
            placeholder={Locale.translate(Translation.SC_BLOOMBERG_INDEX_DESCRIPTION_PLACEHOLDER)}
            onUpdate={updateHandler('description')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            fieldName="code"
            dataTestId={'IndexOverviewPanel-TextInputField-BloombergIndexCode'}
            label={Locale.translate(Translation.TC_BLOOMBERG_INDEX_CODE)}
            value={index.code}
            placeholder={Locale.translate(Translation.SC_BLOOMBERG_INDEX_CODE_PLACEHOLDER)}
            onUpdate={updateHandler('code')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            fieldName="isin"
            dataTestId={'IndexOverviewPanel-TextInputField-Isin'}
            label={Locale.translate(Translation.TC_ISIN)}
            value={index.isin}
            onUpdate={updateHandler('isin')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
            maxLength={isinMaxFieldLength}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            fieldName="name"
            dataTestId={'IndexOverviewPanel-TextInputField-Name'}
            label={Locale.translate(Translation.TC_REFINITIV_BASE_INDEX)}
            value={index.refinitivBaseIndex}
            onUpdate={updateHandler('refinitivBaseIndex')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
            maxLength={indexNameMaxFieldLength}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            fieldName="lastUpdatedAt"
            dataTestId={'IndexOverviewPanel-TextInputField-LastUpdateAt'}
            label={Locale.translate(Translation.TC_LAST_UPDATED_AT)}
            value={Locale.formatTimestamp(index.lastUpdatedAt, userPreference.dateFormat)}
            onUpdate={noop}
            readOnly={true}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            fieldName="lastUpdatedBy"
            dataTestId={'IndexOverviewPanel-TextInputField-LastUpdatedBy'}
            label={Locale.translate(Translation.TC_LAST_UPDATED_BY)}
            value={getUserNameFromUserId(index.lastUpdatedBy)}
            onUpdate={noop}
            readOnly={true}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <label>{Locale.translate(Translation.TC_LAG)}</label>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <NumericInputField
            fieldName="lagAmount"
            dataTestId={'IndexOverviewPanel-TextInputField-LagAmount'}
            label={''}
            value={index.lagAmount}
            isInteger={true}
            maxLength={lagAmountMaxFieldLength}
            onUpdate={updateHandler('lagAmount')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <LagTypeSelectboxField
            fieldName="lagType"
            dataTestId={'IndexOverviewPanel-LagTypeSelectboxField-LagType'}
            label={''}
            value={index.lagType}
            onUpdate={updateHandler('lagType')}
            readOnly={readOnly}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <Heading>
        <span>{Locale.translate(Translation.TC_HISTORICAL_PRICES)}</span>
        {validationIssues && (
          <ValidationIssuesContainer>
            {validationIssues
              .filter(validationIssue => validationIssue.fieldName === 'values')
              .map((validationIssue, issueIndex) => (
                <li key={issueIndex}>
                  {validationIssue.type === ValidationType.MISSING_MANDATORY
                    ? Locale.translate(Translation.SC_NO_PRICES_ERROR)
                    : validationIssue.errorMessage}
                </li>
              ))}
          </ValidationIssuesContainer>
        )}
      </Heading>
      <PriceAdder>
        <StyledDatePicker
          dataTestId={'IndexOverviewPanel-PriceAdder-StyledDatePicker'}
          value={newValidFor}
          dateFormat="MMM yyyy"
          showMonthPicker={true}
          placeholder={Locale.translate(Translation.SC_VALID_FOR_PLACEHOLDER)}
          readOnly={readOnly}
          onUpdate={value => setNewValidFor(value)}
        />
        <PriceEntry>
          <NumericInput
            dataTestId={'IndexOverviewPanel-PriceEntry-NumericInput'}
            value={newPrice}
            formatOptions={PRICE_FORMAT_OPTIONS}
            placeholder={Locale.translate(Translation.SC_PRICE_PLACEHOLDER)}
            readOnly={readOnly}
            onUpdate={value => setNewPrice(value || null)}
          />
          <TextButton
            dataTestId={'IndexOverviewPanel-PriceEntry-TextButton-AddButton'}
            type={TextButtonType.Primary}
            disabled={readOnly || isNil(newValidFor) || isNil(newPrice)}
            onClick={handleAddPrice}>
            {Locale.translate(Translation.TC_BUTTON_ADD)}
          </TextButton>
        </PriceEntry>
      </PriceAdder>
      {Object.keys(index.values).length > 0 ? (
        <PriceContainer>
          <PriceHeader>
            <span>{Locale.translate(Translation.TC_VALID_FOR)}</span>
            <span>{Locale.translate(Translation.TC_INDEX_PRICE)}</span>
            <span />
          </PriceHeader>
          {Object.entries(index.values)
            .sort(([validFor1], [validFor2]) => (moment(validFor1).isBefore(validFor2) ? 1 : -1))
            .map(([validFor, price]) => (
              <PriceRow data-test-id={'IndexOverviewPanel-PriceRow'} key={validFor}>
                <span data-test-id={'IndexOverviewPanel-PriceRow-ValidFor'}>{formatValidFor(validFor)}</span>
                <span data-test-id={'IndexOverviewPanel-PriceRow-IndexPrice'}>{formatPrice(price)}</span>
                <span>
                  <RemovePriceButton
                    dataTestId={'IndexOverviewPanel-PriceRow-RemovePriceButton'}
                    disabled={readOnly}
                    onClick={() => handleRemovePrice(validFor)}>
                    {iconSet.crossIcon}
                  </RemovePriceButton>
                </span>
              </PriceRow>
            ))}
        </PriceContainer>
      ) : (
        <NoPricesContainer>{Locale.translate(Translation.SC_ENTER_PRICES)}</NoPricesContainer>
      )}
    </>
  )
}
